import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { InlineTrumpSelector } from './TrumpSelector';
import { NumberPad } from './NumberPad';
import type { Participant, TrumpSuit, RoundScore, GameMode } from '@/types';
import { SCORING } from '@/types';
import { cn } from '@/lib/utils';

interface RoundScoreDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  participants: Participant[];
  mode: GameMode;
  onSave: (
    scores: RoundScore[],
    trumpSuit: TrumpSuit,
    trumpCallerId: string
  ) => void;
}

type ActiveField = {
  participantId: string;
  type: 'regular' | 'declaration';
};

export function RoundScoreDialog({
  open,
  onOpenChange,
  participants,
  mode,
  onSave,
}: RoundScoreDialogProps) {
  const [trumpCallerId, setTrumpCallerId] = useState<string | null>(null);
  const [trumpSuit, setTrumpSuit] = useState<TrumpSuit | null>(null);
  const [scores, setScores] = useState<Record<string, { regular: number; declaration: number }>>(() => {
    const initial: Record<string, { regular: number; declaration: number }> = {};
    participants.forEach((p) => {
      initial[p.id] = { regular: 0, declaration: 0 };
    });
    return initial;
  });
  const [activeField, setActiveField] = useState<ActiveField | null>(null);
  // Track which participants have manually entered regular points
  const [manualRegularIds, setManualRegularIds] = useState<Set<string>>(new Set());

  // Auto-calc only for 3-player and 4-player modes (total must be 162)
  // In 2-player mode, both players enter their points independently
  const hasAutoCalc = mode === '3_player' || mode === '4_player';

  // Helper to calculate auto-calc for remaining participant(s)
  const calculateAutoCalc = (
    updated: Record<string, { regular: number; declaration: number }>,
    manualIds: Set<string>
  ) => {
    if (!hasAutoCalc) return;

    const autoCalcParticipants = participants.filter((p) => !manualIds.has(p.id));

    if (autoCalcParticipants.length === 1) {
      // Calculate remaining points for the auto-calc participant
      const manualSum = participants
        .filter((p) => manualIds.has(p.id))
        .reduce((sum, p) => sum + updated[p.id].regular, 0);

      const autoId = autoCalcParticipants[0].id;
      updated[autoId] = {
        ...updated[autoId],
        regular: Math.max(0, SCORING.MAX_REGULAR_POINTS - manualSum),
      };
    }
  };

  const handleTrumpSelect = (participantId: string, suit: TrumpSuit) => {
    setTrumpCallerId(participantId);
    setTrumpSuit(suit);
  };

  const handleDigit = useCallback((digit: number) => {
    if (!activeField) return;

    setScores((prev) => {
      const current = prev[activeField.participantId];
      const currentValue = activeField.type === 'regular' ? current.regular : current.declaration;
      const newValue = Math.min(
        currentValue * 10 + digit,
        activeField.type === 'regular' ? SCORING.MAX_REGULAR_POINTS : SCORING.MAX_DECLARATION_POINTS
      );

      const updated = {
        ...prev,
        [activeField.participantId]: {
          ...current,
          [activeField.type]: newValue,
        },
      };

      // Auto-calculate for non-manual regular fields
      if (hasAutoCalc && activeField.type === 'regular') {
        const newManualIds = new Set(manualRegularIds);
        newManualIds.add(activeField.participantId);
        setManualRegularIds(newManualIds);
        calculateAutoCalc(updated, newManualIds);
      }

      return updated;
    });
  }, [activeField, hasAutoCalc, participants, manualRegularIds, calculateAutoCalc]);

  const handleBackspace = useCallback(() => {
    if (!activeField) return;

    setScores((prev) => {
      const current = prev[activeField.participantId];
      const currentValue = activeField.type === 'regular' ? current.regular : current.declaration;
      const newValue = Math.floor(currentValue / 10);

      const updated = {
        ...prev,
        [activeField.participantId]: {
          ...current,
          [activeField.type]: newValue,
        },
      };

      // Auto-calculate for non-manual regular fields
      if (hasAutoCalc && activeField.type === 'regular') {
        calculateAutoCalc(updated, manualRegularIds);
      }

      return updated;
    });
  }, [activeField, hasAutoCalc, manualRegularIds, calculateAutoCalc]);

  const handleClear = useCallback(() => {
    if (!activeField) return;

    setScores((prev) => {
      const current = prev[activeField.participantId];
      const updated = {
        ...prev,
        [activeField.participantId]: {
          ...current,
          [activeField.type]: 0,
        },
      };

      // Auto-calculate for non-manual regular fields
      if (hasAutoCalc && activeField.type === 'regular') {
        calculateAutoCalc(updated, manualRegularIds);
      }

      return updated;
    });
  }, [activeField, hasAutoCalc, manualRegularIds, calculateAutoCalc]);

  const handleDeclarationAdd = useCallback((value: number) => {
    if (!activeField || activeField.type !== 'declaration') return;

    setScores((prev) => {
      const current = prev[activeField.participantId];
      return {
        ...prev,
        [activeField.participantId]: {
          ...current,
          declaration: Math.min(current.declaration + value, SCORING.MAX_DECLARATION_POINTS),
        },
      };
    });
  }, [activeField]);

  const handleFieldSelect = (participantId: string, type: 'regular' | 'declaration') => {
    setActiveField({ participantId, type });
    if (type === 'regular' && hasAutoCalc) {
      setManualRegularIds((prev) => new Set(prev).add(participantId));
    }
  };

  const canSave = trumpCallerId && trumpSuit;

  const handleSave = () => {
    if (!trumpCallerId || !trumpSuit) return;

    const roundScores: RoundScore[] = participants.map((p) => ({
      participantId: p.id,
      regularPoints: scores[p.id].regular,
      declarationPoints: scores[p.id].declaration,
    }));

    onSave(roundScores, trumpSuit, trumpCallerId);
    resetState();
  };

  const resetState = () => {
    setTrumpCallerId(null);
    setTrumpSuit(null);
    const initial: Record<string, { regular: number; declaration: number }> = {};
    participants.forEach((p) => {
      initial[p.id] = { regular: 0, declaration: 0 };
    });
    setScores(initial);
    setActiveField(null);
    setManualRegularIds(new Set());
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetState();
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Round</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {participants.map((participant) => {
            const isAutoCalc = hasAutoCalc && manualRegularIds.size > 0 && !manualRegularIds.has(participant.id);
            const participantScores = scores[participant.id];
            const total = participantScores.regular + participantScores.declaration;
            const isThisTrumpCaller = trumpCallerId === participant.id;

            return (
              <div
                key={participant.id}
                className={cn(
                  'p-3 border rounded-lg space-y-2',
                  isThisTrumpCaller && 'border-primary bg-primary/5'
                )}
              >
                {/* Name and Trump selector row */}
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{participant.name}</span>
                  <InlineTrumpSelector
                    participantId={participant.id}
                    selectedSuit={trumpSuit}
                    isSelected={isThisTrumpCaller}
                    onSelect={handleTrumpSelect}
                  />
                </div>

                {/* Score inputs */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => handleFieldSelect(participant.id, 'regular')}
                    className={cn(
                      'p-2 border rounded-lg text-center transition-colors',
                      activeField?.participantId === participant.id && activeField?.type === 'regular'
                        ? 'border-primary bg-primary/10'
                        : 'border-input hover:bg-muted/50'
                    )}
                  >
                    <p className="text-xs text-muted-foreground">Regular</p>
                    <p className={cn('text-lg font-bold', isAutoCalc && 'text-muted-foreground italic')}>
                      {participantScores.regular}
                      {isAutoCalc && <span className="text-xs ml-1">(auto)</span>}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFieldSelect(participant.id, 'declaration')}
                    className={cn(
                      'p-2 border rounded-lg text-center transition-colors',
                      activeField?.participantId === participant.id && activeField?.type === 'declaration'
                        ? 'border-primary bg-primary/10'
                        : 'border-input hover:bg-muted/50'
                    )}
                  >
                    <p className="text-xs text-muted-foreground">Declarations</p>
                    <p className="text-lg font-bold">{participantScores.declaration}</p>
                  </button>
                </div>

                {/* Total */}
                <div className="text-right text-sm text-muted-foreground">
                  Total: <span className="font-bold text-foreground">{total}</span>
                </div>
              </div>
            );
          })}

          <div className="border-t pt-3">
            <NumberPad
              onDigit={handleDigit}
              onBackspace={handleBackspace}
              onClear={handleClear}
              onDeclarationAdd={handleDeclarationAdd}
              declarationsEnabled={activeField?.type === 'declaration'}
              disabled={!activeField}
            />
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button
            onClick={handleSave}
            disabled={!canSave}
            className="w-full"
          >
            Save Round
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
